import { Box,
  Card,
  CardHeader,
  Chip,
  Divider,
  styled,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  Tooltip,
  Typography } from "@mui/material";
import { useFormik } from "formik";
import { createContext, FC, MouseEventHandler, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BackupScheduleInput, BackupScheduleResponseDto, BackupType, BackupTypeResponseDto, ValidationFrequency } from "~/gen/api/backup";
import { getUserProfile } from "~/reducers/userProfile";
import OdForm, { OnSubmit } from "../formik/OdForm";
import TextField from "../formik/TextField";
import { HourInterval, DayInterval, WeekInterval, MonthInterval, FormikPickableInput, initValueGen, ScheduleInfo } from "./BackupSchedulePicker";
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteForever'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import { CRUD_GET_LIST, useCreate, useDelete, useGetList, useGetMainList, useUpdate } from "ra-core";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { RecPatch } from "~/dataProvider/tools";
import { MutationCallbacks } from "~/dataProvider/types";
import chainOptions from "~/utils/chainOptions";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "~/utils/retrieveAxiosErr";
import { merge } from "lodash";
import Confirm from "../widgets/Confirm";
import * as cronjsMather from '@datasert/cronjs-matcher'
import { buildDateTime, useUserTimeFormat } from '~/utils/userTimeFormat'
import * as Yup from 'yup'
import { useDatabaseInfoContext } from "./DatabaseInfoContext";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { MANAGE_BACKUP_JOBS } from "~/constants";


const SchedulePlanInfo = styled(Stack)(({theme}) => ({
  padding: theme.spacing(3),
  position: 'relative',

  '& > div.MuiSpeedDial-root': {
    display: 'none',
    position: 'absolute', 
    right: 16, 
    top: 'calc(50% - 28px)',
  },
  '&:hover > div.MuiSpeedDial-root': {
    display: 'flex',
  },
}))

const TimezoneTip = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: 0,
  marginLeft: theme.spacing(-3),
  borderTop: `8px solid ${theme.palette.text.secondary}`, 
  borderRight: '8px solid transparent',
  cursor: 'pointer',

  '& + span.MuiTypography-caption': {
    transform: 'matrix(0.8, 0, 0, 0.8, -16, 0)',
  },
  '&:hover + span.MuiTypography-caption': {
    transform: 'matrix(1, 0, 0, 1, 8, 0)',
    marginLeft: theme.spacing(-1),
    '& .more': { display: 'inline' },
  },
}))

const TimezoneTipText = styled(Typography)(({theme}) => ({
  position: 'absolute',
  top: 0,
  transition: 'all 0.1s',
  '& .more' : { display: 'none', }
}))

const SchedulePlanEdit = styled(Stack)(({theme}) => ({
  padding: theme.spacing(3),
  alignItems: 'center',
  position: 'relative',

  '& > .startTime': {
    flexWrap: 'wrap',
    marginLeft: 0,
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(-1.5),
    paddingBottom: theme.spacing(1),
    '& > span:first-of-type': {
      flex: '1 1 100%',
      marginRight: 'auto',
      marginBottom: theme.spacing(1),
    },
    '& > div.MuiFormControl-root:first-of-type': {
      marginLeft: theme.spacing(-1),
    },
  },
  '& .daysInWeek': {
    marginLeft: 0,
    marginBottom: -16,
  },
  '& .daysInWeek-label': {
    marginTop: -12,
  },
  '& .daysInMonth': {
    marginTop: 0,
    marginLeft: 14,
    marginBottom: -20,
  },
  '& .daysInMonth-label': {
    marginTop: -20,
  },
}))


const hourChoices = [...Array(24).keys()].map(i => i.toString().padStart(2, '0'))
const minuteChoices = ['00', '15', '30', '45']


interface BackupSchedulePlanProps {
  databaseId: number;
  createSchedule: (input: BackupScheduleInput, options?: MutationCallbacks) => void,
  updateSchedule: (input: BackupScheduleInput, options?: MutationCallbacks) => void,
  deleteSchedule: (input: BackupScheduleResponseDto, options?: MutationCallbacks) => void,
  addType: BackupType|null,
  setAddType: (x:BackupType|null)=>void,
  editType: BackupType|null,
  setEditType: (x:BackupType|null)=>void,
  innerVersion: Date,
  error: string|null,
  setError: (x:string|null)=>void,
}

const BackupScheduleContext = createContext<BackupSchedulePlanProps>({
  databaseId: 0,
  createSchedule: (input: BackupScheduleInput) => {},
  updateSchedule: (input: BackupScheduleInput) => {},
  deleteSchedule: (input: BackupScheduleResponseDto) => {},
  addType: null,
  setAddType: ()=>{},
  editType: null,
  setEditType: ()=>{},
  innerVersion: new Date(),
  error: null,
  setError: ()=>{},
})

const BackupScheduleContextProvider: FC<{ value: BackupSchedulePlanProps }> = ({ children, value }) => {
  return (
    <BackupScheduleContext.Provider value={value}>
      {children}
    </BackupScheduleContext.Provider>
  )
}

const useBackupScheduleController = (databaseId: number): BackupSchedulePlanProps => {
  const resource = 'backup-schedule'
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  
  const [addType, setAddType] = useState<BackupType|null>(null)
  const [editType, setEditType] = useState<BackupType|null>(null)
  const [innerVersion, setInnerVersion] = useState(new Date())
  const [error, setError] = useState<string|null>(null)

  const [sendCreate] = useCreate(resource)
  const createSchedule: BackupSchedulePlanProps['createSchedule'] = useCallback((input, options) => {
    sendCreate(resource, { ...input }, chainOptions(options, {
      onSuccess: () => { 
        enqueueSnackbar('Backup Schedule created', { variant: 'success' })
        setInnerVersion(new Date())
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Failed to create Backup Schedule: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [sendCreate, databaseId, dispatch, enqueueSnackbar])

  const [sendUpdate,] = useUpdate(resource)
  const updateSchedule: BackupSchedulePlanProps['updateSchedule'] = useCallback((input, options) => {
    sendUpdate(resource, -1, {
      ...input, 
    }, null, chainOptions(options, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Update Backup Schedule succeed', { variant: 'success' })
        setInnerVersion(new Date())
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Update Backup Schedule failed: ${errMsg}`, { variant: 'error' })
      },
    })))
  }, [sendUpdate, databaseId, dispatch, enqueueSnackbar])

  const [sendDelete] = useDelete(resource)
  const deleteSchedule: BackupSchedulePlanProps['deleteSchedule'] = useCallback((input, options) => {
    sendDelete(resource, input.databaseId + '-' + input.backupType, input as any, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Delete Backup Schedule succeed', { variant: 'success' })
        setInnerVersion(new Date())
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Delete Backup Schedule failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [])

  return {
    databaseId,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    addType,
    setAddType,
    editType,
    setEditType,
    innerVersion,
    error,
    setError,
  }
}

const useBackupScheduleContext = () => useContext(BackupScheduleContext);


const validationSchema =
  Yup.object().shape({
    frequency: Yup.number().integer().required('Frequency is required')
      .oneOf([
        ValidationFrequency.Hourly, ValidationFrequency.Daily, ValidationFrequency.Weekly, ValidationFrequency.Monthly
      ]),
    interval: Yup.string().required('Interval is required'),
  })

type ErrorBag = {[k in keyof ScheduleInfo]+?:string}
const weekdays = [ 'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT' ]
const monthdays = [...Array(31).keys()].map(i => (i + 1).toString())

const validate = (values: ScheduleInfo) => {
  const errors: ErrorBag = {}

  if (+values.interval > 1000 || +values.interval < 1) {
    errors['interval'] = 'Invalid interval value'
  }
  if (hourChoices.indexOf(values.startHour) < 0) {
    errors['startHour'] = 'Invalid Hour value'
  }
  if (minuteChoices.indexOf(values.startMinute) < 0) {
    errors['startMinute'] = 'Invalid Minute value'
  }
  switch(values.frequency) {
    case ValidationFrequency.Hourly: { break; }
    case ValidationFrequency.Daily: { break; }
    case ValidationFrequency.Weekly: {
      if (values.daysOfWeek.length === 0) {
        errors['daysOfWeek'] = 'Specify at least 1 day in week'
      } else if (values.daysOfWeek.some(dw => weekdays.indexOf(dw) < 0)) {
        errors['daysOfWeek'] = 'Invalid day(s) of week value'
      } else if (+values.interval > 1 && values.daysOfWeek.length > 1) {
        errors['daysOfWeek'] = 'Allow only 1 day in week'
      }
      break;
    }
    case ValidationFrequency.Monthly: {
      if (monthdays.indexOf(values.dayOfMonth) < 0) {
        errors['dayOfMonth'] = 'Invalid day of month value'
      }
      break;
    }
  }
  return errors;
}
  
const BackupSchedulePlanCard: FC<{databaseId:number}> = ({
  databaseId,
}) => {

  return (
    <BackupScheduleContextProvider value={useBackupScheduleController(databaseId)}>
      <BackupSchedulePlanCardBody databaseId={databaseId} />
    </BackupScheduleContextProvider>
  )
}

const BackupSchedulePlanCardBody: FC<{databaseId:number}> = ({
  databaseId,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_BACKUP_JOBS])
  const { deploymentInfo } = useDatabaseInfoContext()
  const deploymentDeleted = !deploymentInfo || deploymentInfo.isDeleted

  const { addType, setAddType, editType, setEditType, innerVersion, error } = useBackupScheduleContext()
  
  const { data: backupTypeData } = useGetList<BackupTypeResponseDto & RecPatch>('backup-schedule-type', pages(), sorts(), { databaseId })
  const backupTypes = Object.values(backupTypeData || {})
  
  const { data } = useGetMainList<BackupScheduleResponseDto & RecPatch>('backup-schedule', pages(), sorts(), { databaseId, innerVersion }, { action: CRUD_GET_LIST })
  const scheduleInfos = Object.values(data ?? {}).sort((a, b) => (a.backupType ?? 0) - (b.backupType ?? 0))

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl);
  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (backupType: BackupTypeResponseDto) => () => {
    setAddType(backupType.backupType as any)
    setAnchorEl(null);
  };

  const notSpecified = deploymentDeleted ? [] : backupTypes.filter(t => scheduleInfos.every(s => s.backupType !== t.backupType ))
  
  useEffect(() => {
    setAddType(null)
    setEditType(null)
    setAnchorEl(null)
  }, [databaseId, setAddType, setEditType])

  const format = useUserTimeFormat()
  const forecastEvents = (schedules: BackupScheduleResponseDto[]) => {
    return schedules.map(s => {
      let tzOffset = Number.parseInt(s.timeZoneOffset ?? '0')
      let eventTime: Date|string = cronjsMather.getFutureMatches(s.cronExpression ?? '', { matchCount: 1, timezone: tzOffset as any })[0]
      return { 
        backupType: s.backupType, 
        nextEventTime: eventTime,
        timeDisplay: [format(eventTime, 'MMM d'), format(eventTime, 'p')]
      }
    }).sort((a, b) => a.nextEventTime < b.nextEventTime ? -1 : 1)
  }  
  const [forecastedEventTimes, setForecastedEventTimes] = useState<ReturnType<typeof forecastEvents>>(forecastEvents(scheduleInfos));
  const preDatabaseIdRef = useRef(-1)
  const scheduleInfosRef = useRef(scheduleInfos);
  scheduleInfosRef.current = preDatabaseIdRef.current === databaseId ? scheduleInfos : [];
  preDatabaseIdRef.current = databaseId
  useEffect(() => {
    if (deploymentDeleted) {
      setForecastedEventTimes(
        scheduleInfosRef.current.map(s => ({
          backupType: s.backupType,
          nextEventTime: 'N/A',
          timeDisplay: ['N/A'],
        }))
      )
      return
    }
    setForecastedEventTimes(forecastEvents(scheduleInfosRef.current))

    const timer = window.setInterval(() => {
      setForecastedEventTimes(forecastEvents(scheduleInfosRef.current))
    }, 60 * 1000)

    return () => {
      timer && clearInterval(timer)
    }
  }, [deploymentDeleted, JSON.stringify(scheduleInfosRef.current)])

  return (
    <Card>
      <CardHeader 
        title="Backup Schedule" 
        subheader={
          <>
            {scheduleInfos.length > 0 &&
              <Typography
                variant="caption"
                color="GrayText"
                sx={{ position: 'relative', bottom: '-10px' }}
              >
                Next up
              </Typography> }         
            {error &&
              <Typography 
                component="span"
                variant="body2" 
                color="error" 
                sx={{ position: 'absolute', right: 0, bottom: '-10px' }}
              >
                {error}
              </Typography>}
          </>
        }
        action={
          hasWritePermit && notSpecified.length > 0 ?
          <>
            <Tooltip title="Add">
              <IconButton color="primary" onClick={handleClick} disabled={addType !== null || editType !== null}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Menu
              aria-labelledby="add-backup-schedules"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {notSpecified.map(t =>
                <MenuItem key={t.backupType} onClick={handleClose(t)}>{t.text}</MenuItem>
              )}
            </Menu>
          </> :
          null
        }
        sx={{
          '& > .MuiCardHeader-content': { position: 'relative' },
        }}
      />
      <Divider />
      <Stack direction="column" divider={<Divider />} >
        {addType !== null && hasWritePermit &&
          <SchedulePlanEditLine backupType={backupTypeData[addType]} />}
        
        {forecastedEventTimes.map(t => { 
          const s = scheduleInfos.find(s => s.backupType === t.backupType)
          if (!s) return null;
          const bt = backupTypeData[s.backupType ?? 0]
          if (!bt) return null;

          return (editType === s.backupType && hasWritePermit ?
          <SchedulePlanEditLine key={s.backupType} preData={s} backupType={bt} /> :

          <SchedulePlanInfoLine key={s.backupType} plan={s} backupType={bt} timeDisplay={t.timeDisplay} editable={hasWritePermit && !deploymentDeleted} />)
        })}
      </Stack>
    </Card>
  )
}

export default BackupSchedulePlanCard;


const weekdayName: Record<string, string> = {
  'SUN': 'Sunday',
  'MON': 'Monday',
  'TUE': 'Tuesday',
  'WED': 'Wednesday',
  'THU': 'Thurday',
  'FRI': 'Friday',
  'SAT': 'Saturday',
}

const SchedulePlanInfoLine: FC<{plan:BackupScheduleResponseDto & RecPatch, backupType: BackupTypeResponseDto, timeDisplay: string[], editable?: boolean}> = ({
  plan,
  backupType,
  timeDisplay,
  editable = true,
}) => {
  const { deleteSchedule, setEditType, addType, editType } = useBackupScheduleContext()
  const detailContent =
      plan.frequency === ValidationFrequency.Hourly ? [`Every ${plan.interval} hour(s)`]
    : plan.frequency === ValidationFrequency.Daily  ? [`Every ${plan.interval} day(s)`]
    : plan.frequency === ValidationFrequency.Weekly ?
      [
        `Every ${plan.interval} week(s) on`,
        (plan.daysOfWeek || '').indexOf(',') > -1
        ? plan.daysOfWeek?.replaceAll(',', ', ')
        : weekdayName[plan.daysOfWeek as any]
      ]
    : plan.frequency === ValidationFrequency.Monthly ?
      [`Every ${plan.interval} month(s) on`, `day ${plan.dayOfMonth}`]
    : []

  const startTimeText = useMemo(() => {
    return `${(plan.startHour ?? 0).toString().padStart(2, '0')} : ${(plan.startMinute ?? 0).toString().padStart(2, '0')}`
  }, [plan])

  const [openConfirm, setOpenConfirm] = useState(false)
  const handleDelete = () => {
    deleteSchedule(plan, {
      onSuccess: () => setOpenConfirm(false)
    })
  }

  return (
    <SchedulePlanInfo direction="row" spacing={2} >
      <Box sx={{ flex: '0 0 100px', mx: -1 }}>
        <Typography
          align="center"
          color="textSecondary"
          variant="subtitle2"
        >
          {timeDisplay[0].toUpperCase()}
        </Typography>
        {timeDisplay[1] &&
          <Typography
            align="center"
            color="textSecondary"
            variant="h6"
          >
            {timeDisplay[1]}
          </Typography>}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          align="center"
          color="textPrimary"
          variant="subtitle2"
        >
          <Chip label={backupType.text} color="primary" variant="outlined" />
        </Typography>
        <Typography
          align="center"
          color="textSecondary"
          variant="body2"
        >
          {ValidationFrequency[plan.frequency ?? ValidationFrequency.Daily]} Backup
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          color="textPrimary"
          variant="subtitle2"
        >
          {detailContent[0]}
        </Typography>
        {detailContent[1] &&
          <Typography
            color="textSecondary"
            variant="body2"
          >
            {detailContent[1]}
          </Typography>}
      </Box>
      <Box sx={{ flex: 1 }}>
        <TimezoneTip />
        <TimezoneTipText variant="caption">
          <span>{friendlyDisplayOffset(plan.timeZoneOffset ?? '0') || '+'}</span>
          <span className="more">, {plan.timeZoneName ?? 'Coordinated Universal Time'}</span>
        </TimezoneTipText>
        <Typography
          color="textPrimary"
          variant="subtitle2"
        >
          Start Time
        </Typography>
        <Typography
          color="textSecondary"
          variant="body2"
        >
          {startTimeText}
        </Typography>
      </Box>
      {editable && addType === null && editType === null &&
        <SpeedDial 
          ariaLabel="Backup Schedule Edit Actions"
          direction="left"
          FabProps={{
            size: 'small',
            color: 'default',
          }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction 
            icon={<EditIcon />}
            tooltipTitle="Edit"
            onClick={() => setEditType(plan.backupType as any)}
          />
          <SpeedDialAction 
            icon={<DeleteIcon />}
            tooltipTitle="Delete"
            onClick={() => setOpenConfirm(true)}
          />
        </SpeedDial>}
      <Confirm
        isOpen={openConfirm}
        title={"Delete Backup Schedule"}
        onConfirm={handleDelete}
        onClose={() => setOpenConfirm(false)}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this {backupType.text} Backup Schedule.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </SchedulePlanInfo> 
  )
}

const friendlyDisplayOffset = (offsetStr:string|number) => {
  let offset = Number.parseInt(offsetStr as any)
  if (isNaN(offset)) { throw 'Invalid TimezoneOffset' }
  let n = Math.abs(offset)
  let sign = n === offset ? '+' : '-'
  let h = (n / 60)^0
  let m = n % 60
  return `${sign}${h}:${(m+'').padStart(2, '0')}`
}


const SchedulePlanEditLine: FC<{preData?:BackupScheduleResponseDto|undefined, backupType: BackupTypeResponseDto}> = ({
  preData,
  backupType,
}) => {
  const { databaseId, createSchedule, updateSchedule, setAddType, setEditType, setError } = useBackupScheduleContext()

  const backupTypeInt = preData?.backupType || backupType.backupType !== undefined ? backupType.backupType : BackupType.Full
  const initialValues = initValueGen(preData)
  const onSubmit: OnSubmit<typeof initialValues> = (values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        if (preData) {
          setEditType(null)
        } else {
          setAddType(null)
        }
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const mutation = preData ? updateSchedule : createSchedule

    const input = merge({
      databaseId,
      backupType: backupTypeInt,
      frequency: values.frequency,
      interval: +values.interval,
      startHour: +values.startHour,
      startMinute: +values.startMinute,
    },
      values.frequency === ValidationFrequency.Weekly
      ? { daysOfWeek: values.daysOfWeek.join(',') }
      : values.frequency === ValidationFrequency.Monthly
      ? { dayOfMonth: values.dayOfMonth }
      : {}
    ) as BackupScheduleInput;

    mutation(input, options)
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validate, validationSchema })
  const { values, errors, dirty } = formikCtrl

  useEffect(() => {
    const ks = Object.keys(errors)
    if (ks.length > 0 && dirty) {
      //@ts-ignore
      setError(errors[ks[0]])
    } else {
      setError(null)
    }
  }, [Object.keys(errors).length])

  const handleCancel = () => {
    if (preData) {
      setEditType(null)
    } else {
      setAddType(null)
    }
    setError(null)
  }

  const user = useSelector(getUserProfile)
  
  return (
    <OdForm formikCtrl={formikCtrl}>
      <SchedulePlanEdit direction="row" spacing={2}>
        <Box sx={{ flex: '0 0 100px', mx: -1, display: 'flex', justifyContent: 'space-around' }}>
          <Chip label={backupType.text} color="primary" variant="outlined" />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around' }}>
          <Box sx={{ width: 150 }}>
            <TextField 
              label="Frequency*" 
              name="frequency" 
              select
              size="small"
              fullWidth
            >
              <MenuItem value={ValidationFrequency.Hourly}>Hourly</MenuItem>
              <MenuItem value={ValidationFrequency.Daily}>Daily</MenuItem>
              <MenuItem value={ValidationFrequency.Weekly}>Weekly</MenuItem>
              <MenuItem value={ValidationFrequency.Monthly}>Monthly</MenuItem>
            </TextField>
          </Box>
        </Box>
        <Box 
          sx={{
            mx: -2,
            mt: 2,
            px: 2,
            py: 1,
            flex: 1,
          }}
        >
          {values.frequency === ValidationFrequency.Hourly
          ? <HourInterval />
          : values.frequency === ValidationFrequency.Daily
          ? <DayInterval />
          : values.frequency === ValidationFrequency.Weekly
          ? <WeekInterval />
          : values.frequency === ValidationFrequency.Monthly
          ? <MonthInterval />
          : null
          }              
        </Box>
        <Box 
          className="startTime"
          sx={{
            mx: -2,
            px: 2,
            pt: 1,
            pb: 2,
            display: 'flex',
            alignItems: 'baseline',
            flex: 1
          }}
        >
          <TimezoneTip />
          <TimezoneTipText variant="caption">
            {friendlyDisplayOffset(preData?.timeZoneOffset !== undefined ? (preData?.timeZoneOffset ?? 0) : (user?.timeZoneOffset ?? '0'))}
            <span className="more">, {(preData?.timeZoneName ?? user?.timeZoneName) ?? 'Coordinated Universal Time'}</span>
          </TimezoneTipText>
          <Typography
            component="span"
            color="textPrimary"
            variant="subtitle2"
          >
            Start Time
          </Typography>
          <FormControl>
            <FormikPickableInput
              allowEdit={false}
              name="startHour"
              choices={hourChoices}
              choiceRowLength={6}
              maxRows={5}
            />
          </FormControl>
          <Typography
            component="span"
            variant="body2"
          >
            :
          </Typography>
          <FormControl>
            <FormikPickableInput
              allowEdit={false}
              name="startMinute"
              choices={minuteChoices}
              choiceRowLength={4}
            />
          </FormControl>
        </Box>
        <Box sx={{ position: 'absolute', right: 16, top: 'calc(50% - 22px)'}}>
          <Tooltip title="Save">
            <IconButton color="primary" type="submit">
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton color="secondary" onClick={handleCancel}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </SchedulePlanEdit>
    </OdForm>
  )
}
